import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Row, Col, Table, DatePicker, Modal, Spin, Input, Popconfirm, Form, Select, Tooltip, Skeleton } from 'antd';
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  LeftOutlined,
  RightOutlined,
  EnvironmentOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  UnorderedListOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SunOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import MainLayout from '../components/MainLayout';
import { attendanceService, EmployeeAttendanceInfo, Attendance, DayAttendance, Location } from '../services/attendanceService';
import { publicHolidayService, PublicHoliday } from '../services/publicHolidayService';
import { projectService, Project } from '../services/projectService';
import dayjs from 'dayjs';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';
import { PageHeader, StatTile, SectionCard, EmptyState, FilterChips, PersonAvatar, Pill, Tone } from '../components/ui';
import './AttendanceEmployeeInfo.css';

// Fix for default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const { TextArea } = Input;

type DayFilter = 'records' | 'all';

/** One calendar day of the shown month, with everything that explains it. */
interface DayEntry {
  date: string;
  day: dayjs.Dayjs;
  attendance?: DayAttendance;
  isWeekend: boolean;
  holiday?: PublicHoliday;
  vacationType?: string;
}

const AttendanceEmployeeInfo: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [attendanceInfo, setAttendanceInfo] = useState<EmployeeAttendanceInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [dayFilter, setDayFilter] = useState<DayFilter>('records');
  const [selectedAttendance, setSelectedAttendance] = useState<Attendance | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [selectedAttendanceForClose, setSelectedAttendanceForClose] = useState<Attendance | null>(null);
  const [checkOutTime, setCheckOutTime] = useState<dayjs.Dayjs | null>(null);
  const [notes, setNotes] = useState('');
  const [closingLoading, setClosingLoading] = useState(false);
  const [tableModalVisible, setTableModalVisible] = useState(false);
  const [selectedAttendanceForTable, setSelectedAttendanceForTable] = useState<Attendance | null>(null);
  const [tableLocations, setTableLocations] = useState<Location[]>([]);
  const [loadingTableLocations, setLoadingTableLocations] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [newAttendanceModalVisible, setNewAttendanceModalVisible] = useState(false);
  const [newAttendanceForm] = Form.useForm();
  const [creatingAttendance, setCreatingAttendance] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);

  const fetchAttendanceInfo = async () => {
    try {
      setLoading(true);
      const data = await attendanceService.getEmployeeAttendanceInfo(
        Number(id),
        selectedMonth.format('YYYY'),
        selectedMonth.format('MM')
      );
      setAttendanceInfo(data);
    } catch (error) {
      console.error('Failed to fetch attendance info:', error);
      toast.error(t('attendance.fetchAttendanceError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicHolidays = async () => {
    try {
      const data = await publicHolidayService.getAllHolidays();
      // Filter holidays for the selected month and year
      const filteredHolidays = data.filter(holiday => {
        const holidayDate = dayjs(holiday.date);
        return holidayDate.month() + 1 === selectedMonth.month() + 1 &&
               holidayDate.year() === selectedMonth.year();
      });
      setPublicHolidays(filteredHolidays);
    } catch (error) {
      console.error('Failed to fetch public holidays:', error);
      toast.error(t('attendance.fetchHolidaysError'));
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error(t('attendance.fetchProjectsError'));
    }
  };

  useEffect(() => {
    fetchAttendanceInfo();
    fetchPublicHolidays();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedMonth]);

  const handleMonthChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setSelectedMonth(date);
    }
  };

  const handleViewLocations = async (attendance: Attendance) => {
    setSelectedAttendance(attendance);
    setMapVisible(true);
    setLoadingLocations(true);
    try {
      const data = await attendanceService.getAttendanceLocations(attendance.id);
      setLocations(data.locations);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error(t('attendance.fetchLocationsError'));
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleViewLocationsTable = async (attendance: Attendance) => {
    setSelectedAttendanceForTable(attendance);
    setTableModalVisible(true);
    setLoadingTableLocations(true);
    try {
      const data = await attendanceService.getAttendanceLocations(attendance.id);
      setTableLocations(data.locations);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error(t('attendance.fetchLocationsError'));
    } finally {
      setLoadingTableLocations(false);
    }
  };

  const handleCloseAttendance = async () => {
    if (!selectedAttendanceForClose || !checkOutTime) return;

    try {
      setClosingLoading(true);
      const checkOutDateTime = dayjs(selectedAttendanceForClose.check_in)
        .hour(checkOutTime.hour())
        .minute(checkOutTime.minute())
        .second(checkOutTime.second());

      await attendanceService.closeAttendance(
        selectedAttendanceForClose.id,
        checkOutDateTime.format('YYYY-MM-DD HH:mm:ss'),
        notes
      );
      toast.success(t('attendance.attendanceRecordClosed'));
      setCloseModalVisible(false);
      fetchAttendanceInfo();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('attendance.closeAttendanceError'));
    } finally {
      setClosingLoading(false);
    }
  };

  const showCloseModal = (attendance: Attendance) => {
    setSelectedAttendanceForClose(attendance);
    setCheckOutTime(dayjs(attendance.check_in).hour(dayjs().hour()).minute(dayjs().minute()).second(dayjs().second()));
    setNotes('');
    setCloseModalVisible(true);
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  };

  /** Shorter form for pills and tiles, where seconds only add noise. */
  const formatHours = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  };

  const handleDeleteAttendance = async (attendanceId: number) => {
    try {
      setDeletingId(attendanceId);
      await attendanceService.deleteAttendance(attendanceId);
      toast.success(t('attendance.attendanceRecordDeleted'));
      fetchAttendanceInfo();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('attendance.deleteAttendanceError'));
    } finally {
      setDeletingId(null);
    }
  };

  const getVacationStatus = (vacationType: string): string => {
    switch (vacationType.toLowerCase()) {
      case 'Jahresurlaub':
        return t('vacations.annualLeave');
      case 'Krankheitsurlaub':
        return t('vacations.sickLeave');
      default:
        return `${t('attendance.vacation')} (${vacationType})`;
    }
  };

  const handleExportPDF = () => {
    if (!attendanceInfo) return;

    const doc = new jsPDF() as any;
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add title
    doc.setFontSize(20);
    doc.text(t('attendance.attendanceReport'), pageWidth / 2, 20, { align: 'center' });

    // Add employee info
    doc.setFontSize(16);
    doc.text(`${attendanceInfo.user.employee.name} ${attendanceInfo.user.employee.surname}`, pageWidth / 2, 30, { align: 'center' });
    doc.setFontSize(12);

    // Add period
    doc.setFontSize(14);
    doc.text(`${t('attendance.period')}: ${selectedMonth.format('MMMM YYYY')}`, pageWidth / 2, 44, { align: 'center' });

    // Add summary statistics
    doc.setFontSize(12);
    doc.text(`${t('attendance.summaryStatistics')}:`, 14, 54);

    const stats = [
      [t('attendance.totalRecords'), attendanceInfo.total_records.toString()],
      [t('attendance.totalWorkingDays'), attendanceInfo.total_days.toString()],
      [t('attendance.totalHours'), formatDuration(attendanceInfo.total_duration_seconds)],
    ];

    autoTable(doc, {
      startY: 60,
      head: [[t('attendance.metric'), t('attendance.value')]],
      body: stats,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 80 }
      }
    });

    // Generate all days of the month
    const startOfMonth = selectedMonth.startOf('month');
    const endOfMonth = selectedMonth.endOf('month');
    const allDays = [];

    let currentDay = startOfMonth;
    while (currentDay.isBefore(endOfMonth) || currentDay.isSame(endOfMonth, 'day')) {
      const dateStr = currentDay.format('YYYY-MM-DD');
      const currentDayUnix = currentDay.unix();
      const dayAttendance = attendanceInfo.attendances[dateStr];
      const isPublicHoliday = publicHolidays.some(holiday =>
        dayjs(holiday.date).format('YYYY-MM-DD') === dateStr
      );
      const vacationInfo = attendanceInfo.vacations?.find(vacation => {
        const vacationStart = dayjs(vacation.start_date);
        const vacationEnd = dayjs(vacation.end_date);
        const startUnix = vacationStart.unix();
        const endUnix = vacationEnd.unix();
        return currentDayUnix >= startUnix && currentDayUnix <= endUnix;
      });
      const isVacationDay = !!vacationInfo;
      const isWeekend = currentDay.day() === 0 || currentDay.day() === 6; // Sunday = 0, Saturday = 6

      let status = t('attendance.noRecord');
      let duration = '0h 0m 0s';
      let records = '0';

      if (dayAttendance) {
        // If there's attendance record, show the actual data but mark status appropriately
        duration = formatDuration(dayAttendance.total_duration_seconds);
        records = dayAttendance.total_records.toString();

        if (isWeekend) {
          status = t('attendance.weekend');
        } else if (isPublicHoliday) {
          status = t('attendance.publicHoliday');
        } else if (isVacationDay) {
          status = getVacationStatus(vacationInfo!.type);
        } else {
          status = dayAttendance.records.some(r => !r.check_out) ? t('attendance.inProgress') : t('attendance.completed');
        }
      } else {
        // No attendance record
        if (isWeekend) {
          status = t('attendance.weekend');
          duration = 'N/A';
          records = 'N/A';
        } else if (isPublicHoliday) {
          status = t('attendance.publicHoliday');
          duration = 'N/A';
          records = 'N/A';
        } else if (isVacationDay) {
          status = getVacationStatus(vacationInfo!.type);
          duration = 'N/A';
          records = 'N/A';
        } else {
          status = t('attendance.noRecord');
          duration = '0h 0m 0s';
          records = '0';
        }
      }

      allDays.push([
        currentDay.format('MMM DD, YYYY'),
        currentDay.format('dddd'),
        records,
        duration,
        status
      ]);

      currentDay = currentDay.add(1, 'day');
    }

    // Add attendance records table
    doc.setFontSize(12);
    doc.text(`${t('attendance.dailyAttendanceRecords')}:`, 14, doc.lastAutoTable.finalY + 10);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 15,
      head: [[t('attendance.date'), t('attendance.time'), t('attendance.records'), t('attendance.duration'), t('attendance.status')]],
      body: allDays,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 36 },
        1: { cellWidth: 36 },
        2: { cellWidth: 36 },
        3: { cellWidth: 36 },
        4: { cellWidth: 36 }
      },
      didParseCell: function(data: any) {
        // Color coding for different statuses
        if (data.column.index === 4) { // Status column
          const cellText = data.cell.text[0];
          if (cellText === t('attendance.publicHoliday')) {
            data.cell.styles.textColor = [255, 152, 0]; // Orange text
          } else if (cellText === t('vacations.annualLeave') ||
                     cellText === t('vacations.sickLeave') ||
                     cellText.includes(t('attendance.vacation'))) {
            data.cell.styles.textColor = [76, 175, 80]; // Green text
          } else if (cellText === t('attendance.weekend')) {
            data.cell.styles.textColor = [156, 39, 176]; // Purple text
          } else if (cellText === t('attendance.inProgress')) {
            data.cell.styles.textColor = [33, 150, 243]; // Blue text
          } else if (cellText === t('attendance.completed')) {
            data.cell.styles.textColor = [139, 195, 74]; // Light green text
          } else if (cellText === t('attendance.noRecord')) {
            data.cell.styles.textColor = [244, 67, 54]; // Red text
          }
        }
      }
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Save the PDF
    doc.save(`attendance_report_${attendanceInfo.user.employee.name}_${attendanceInfo.user.employee.surname}_${selectedMonth.format('YYYY-MM')}.pdf`);
  };

  const handleCreateAttendance = async (values: any) => {
    if (!attendanceInfo) return;

    try {
      setCreatingAttendance(true);
      const checkInDateTime = dayjs(values.date).hour(values.checkIn.hour()).minute(values.checkIn.minute()).second(values.checkIn.second());
      const checkOutDateTime = dayjs(values.date).hour(values.checkOut.hour()).minute(values.checkOut.minute()).second(values.checkOut.second());

      await attendanceService.storeAttendance(
        attendanceInfo.user.id,
        checkInDateTime.format('YYYY-MM-DD HH:mm:ss'),
        checkOutDateTime.format('YYYY-MM-DD HH:mm:ss'),
        values.project
      );

      toast.success(t('attendance.attendanceRecordCreated'));
      setNewAttendanceModalVisible(false);
      newAttendanceForm.resetFields();
      fetchAttendanceInfo();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(key => {
          toast.error(errors[key][0]);
        });
      } else {
        toast.error(error.response?.data?.message || t('attendance.createAttendanceError'));
      }
    } finally {
      setCreatingAttendance(false);
    }
  };

  /** Every day of the month with its records, holiday and leave context — the same picture the PDF prints. */
  const days: DayEntry[] = useMemo(() => {
    if (!attendanceInfo) return [];
    const result: DayEntry[] = [];
    const end = selectedMonth.endOf('month');
    let current = selectedMonth.startOf('month');

    while (current.isBefore(end) || current.isSame(end, 'day')) {
      const date = current.format('YYYY-MM-DD');
      const unix = current.unix();
      result.push({
        date,
        day: current,
        attendance: attendanceInfo.attendances[date],
        isWeekend: current.day() === 0 || current.day() === 6,
        holiday: publicHolidays.find((holiday) => dayjs(holiday.date).format('YYYY-MM-DD') === date),
        vacationType: attendanceInfo.vacations?.find(
          (vacation) => unix >= dayjs(vacation.start_date).unix() && unix <= dayjs(vacation.end_date).unix(),
        )?.type,
      });
      current = current.add(1, 'day');
    }
    return result;
  }, [attendanceInfo, publicHolidays, selectedMonth]);

  const daysWithRecords = days.filter((entry) => entry.attendance);
  const visibleDays = dayFilter === 'all' ? days : daysWithRecords;
  const leaveDays = days.filter((entry) => entry.vacationType).length;

  const monthLabel = selectedMonth.toDate().toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  const isCurrentMonth = selectedMonth.isSame(dayjs(), 'month');
  const employee = attendanceInfo?.user.employee;

  return (
    <MainLayout bare>
      <button type="button" className="e22-link-button att-back" onClick={() => navigate('/attendance')}>
        <ArrowLeftOutlined /> {t('common.back')}
      </button>

      {loading && !attendanceInfo ? (
        <div className="e22-card e22-section">
          <Skeleton active avatar paragraph={{ rows: 6 }} />
        </div>
      ) : attendanceInfo && employee ? (
        <>
          <PageHeader
            title={`${employee.name} ${employee.surname}`}
            subtitle={
              <span className="att-contact">
                {employee.email ? (
                  <a href={`mailto:${employee.email}`}>
                    <MailOutlined /> {employee.email}
                  </a>
                ) : null}
                {employee.phone ? (
                  <a href={`tel:${employee.phone}`}>
                    <PhoneOutlined /> {employee.phone}
                  </a>
                ) : null}
              </span>
            }
            actions={
              <>
                <Button icon={<FilePdfOutlined />} onClick={handleExportPDF}>
                  {t('attendance.exportPDF')}
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewAttendanceModalVisible(true)}>
                  {t('attendance.addAttendance')}
                </Button>
              </>
            }
          />

          {/* Month picker */}
          <div className="att-monthbar e22-card">
            <PersonAvatar
              name={employee.name}
              surname={employee.surname}
              imageUrl={(employee as any).thumbnailUrl || employee.imageUrl}
              size={44}
            />
            <div className="att-monthbar-steppers">
              <Tooltip title={t('attendance.previousYear')}>
                <Button
                  type="text"
                  icon={<span className="att-double">«</span>}
                  onClick={() => setSelectedMonth(selectedMonth.subtract(1, 'year'))}
                  aria-label={t('attendance.previousYear')}
                />
              </Tooltip>
              <Tooltip title={t('attendance.previousMonth')}>
                <Button
                  type="text"
                  icon={<LeftOutlined />}
                  onClick={() => setSelectedMonth(selectedMonth.subtract(1, 'month'))}
                  aria-label={t('attendance.previousMonth')}
                />
              </Tooltip>
              <DatePicker
                picker="month"
                value={selectedMonth}
                onChange={handleMonthChange}
                allowClear={false}
                format="MMMM YYYY"
                className="att-monthpicker"
                inputReadOnly
              />
              <Tooltip title={t('attendance.nextMonth')}>
                <Button
                  type="text"
                  icon={<RightOutlined />}
                  onClick={() => setSelectedMonth(selectedMonth.add(1, 'month'))}
                  aria-label={t('attendance.nextMonth')}
                />
              </Tooltip>
              <Tooltip title={t('attendance.nextYear')}>
                <Button
                  type="text"
                  icon={<span className="att-double">»</span>}
                  onClick={() => setSelectedMonth(selectedMonth.add(1, 'year'))}
                  aria-label={t('attendance.nextYear')}
                />
              </Tooltip>
            </div>
            {!isCurrentMonth && (
              <button type="button" className="e22-link-button" onClick={() => setSelectedMonth(dayjs())}>
                {t('dashboard.backToThisMonth')}
              </button>
            )}
          </div>

          {/* Month summary */}
          <Row gutter={[16, 16]} className="att-stats">
            <Col xs={12} lg={6}>
              <StatTile
                label={t('attendance.totalHours')}
                value={formatHours(attendanceInfo.total_duration_seconds)}
                icon={<ClockCircleOutlined />}
                tone="primary"
                loading={loading}
              />
            </Col>
            <Col xs={12} lg={6}>
              <StatTile
                label={t('attendance.totalWorkingDays')}
                value={attendanceInfo.total_days}
                icon={<CalendarOutlined />}
                tone="success"
                loading={loading}
              />
            </Col>
            <Col xs={12} lg={6}>
              <StatTile
                label={t('attendance.totalRecords')}
                value={attendanceInfo.total_records}
                icon={<UnorderedListOutlined />}
                tone="violet"
                loading={loading}
              />
            </Col>
            <Col xs={12} lg={6}>
              <StatTile
                label={t('attendance.leaveDays')}
                value={leaveDays}
                icon={<SunOutlined />}
                tone="warning"
                loading={loading}
              />
            </Col>
          </Row>

          <SectionCard
            title={t('attendance.attendanceHistory')}
            icon={<ClockCircleOutlined />}
            subtitle={monthLabel}
            action={
              <FilterChips<DayFilter>
                ariaLabel={t('attendance.attendanceHistory')}
                value={dayFilter}
                onChange={setDayFilter}
                options={[
                  { key: 'records', label: t('attendance.daysWithRecords'), count: daysWithRecords.length },
                  { key: 'all', label: t('attendance.allDays'), count: days.length },
                ]}
              />
            }
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 5 }} />
            ) : visibleDays.length === 0 ? (
              <EmptyState icon={<ClockCircleOutlined />} text={t('attendance.noRecordsThisMonth')} />
            ) : (
              <div className="att-days">
                {visibleDays.map((entry) => {
                  const records = entry.attendance?.records ?? [];
                  const inProgress = records.some((record) => !record.check_out);
                  const tags: { tone: Tone; label: string }[] = [];
                  if (entry.holiday) tags.push({ tone: 'warning', label: entry.holiday.name || t('attendance.publicHoliday') });
                  if (entry.vacationType) tags.push({ tone: 'warning', label: getVacationStatus(entry.vacationType) });
                  if (entry.isWeekend) tags.push({ tone: 'violet', label: t('attendance.weekend') });
                  if (records.length > 0) {
                    tags.push(
                      inProgress
                        ? { tone: 'success', label: t('attendance.inProgress') }
                        : { tone: 'primary', label: t('attendance.completed') },
                    );
                  } else if (!entry.isWeekend && !entry.holiday && !entry.vacationType) {
                    tags.push({ tone: 'danger', label: t('attendance.noRecord') });
                  }

                  return (
                    <article
                      key={entry.date}
                      className={`att-day ${records.length === 0 ? 'att-day--empty' : ''} ${
                        entry.day.isSame(dayjs(), 'day') ? 'att-day--today' : ''
                      }`}
                    >
                      <div className="att-day-date">
                        <span className="att-day-number">{entry.day.format('D')}</span>
                        <span className="att-day-weekday">
                          {entry.day.toDate().toLocaleDateString(i18n.language, { weekday: 'short' })}
                        </span>
                      </div>

                      <div className="att-day-body">
                        <div className="att-day-head">
                          {tags.map((tag) => (
                            <Pill key={tag.label} tone={tag.tone} live={tag.label === t('attendance.inProgress')}>
                              {tag.label}
                            </Pill>
                          ))}
                          {entry.attendance ? (
                            <span className="att-day-total">
                              {formatHours(entry.attendance.total_duration_seconds)}
                            </span>
                          ) : null}
                        </div>

                        {records.map((record, index) => (
                          <div className="att-record" key={record.id}>
                            <span className="att-record-index">{index + 1}</span>
                            <span className="att-record-time">
                              {dayjs(record.check_in).format('HH:mm')}
                              {' – '}
                              {record.check_out ? (
                                dayjs(record.check_out).format('HH:mm')
                              ) : (
                                <span className="att-open">{t('attendance.inProgress')}</span>
                              )}
                            </span>
                            <span className="att-record-duration">
                              {record.duration_seconds ? formatHours(record.duration_seconds) : ''}
                            </span>
                            <span className="att-record-actions">
                              <Tooltip title={t('attendance.viewLocations')}>
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<EnvironmentOutlined />}
                                  onClick={() => handleViewLocations(record)}
                                />
                              </Tooltip>
                              <Tooltip title={t('attendance.viewLocationsList')}>
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<UnorderedListOutlined />}
                                  onClick={() => handleViewLocationsTable(record)}
                                />
                              </Tooltip>
                              {!record.check_out && (
                                <Tooltip title={t('attendance.closeAttendance')}>
                                  <Button
                                    size="small"
                                    type="text"
                                    icon={<CloseCircleOutlined />}
                                    onClick={() => showCloseModal(record)}
                                  />
                                </Tooltip>
                              )}
                              <Popconfirm
                                title={t('attendance.deleteConfirmation')}
                                description={t('attendance.deleteDescription')}
                                onConfirm={() => handleDeleteAttendance(record.id)}
                                okText={t('common.yes')}
                                cancelText={t('common.no')}
                                okButtonProps={{ danger: true }}
                              >
                                <Tooltip title={t('attendance.deleteAttendance')}>
                                  <Button
                                    size="small"
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={deletingId === record.id}
                                  />
                                </Tooltip>
                              </Popconfirm>
                            </span>
                          </div>
                        ))}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </>
      ) : null}

      <Modal
        title={`${t('attendance.attendanceLocations')} - ${selectedAttendance ? dayjs(selectedAttendance.check_in).format('DD MMM YYYY') : ''}`}
        open={mapVisible}
        onCancel={() => setMapVisible(false)}
        width={860}
        footer={null}
      >
        {loadingLocations ? (
          <div className="att-modal-center">
            <Spin size="large" />
          </div>
        ) : locations.length > 0 ? (
          <div className="att-map">
            <MapContainer
              center={[locations[0].latitude, locations[0].longitude]}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {locations.map((location) => (
                <Marker key={location.id} position={[location.latitude, location.longitude]}>
                  <Popup>
                    <div>
                      <p>{t('attendance.time')}: {dayjs(location.created_at).format('HH:mm:ss')}</p>
                      <p>{location.address}</p>
                      <p>{location.latitude}, {location.longitude}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        ) : (
          <EmptyState icon={<EnvironmentOutlined />} text={t('attendance.noLocationData')} />
        )}
      </Modal>

      <Modal
        title={`${t('attendance.locationsList')} - ${selectedAttendanceForTable ? dayjs(selectedAttendanceForTable.check_in).format('DD MMM YYYY') : ''}`}
        open={tableModalVisible}
        onCancel={() => setTableModalVisible(false)}
        width={800}
        footer={null}
      >
        {loadingTableLocations ? (
          <div className="att-modal-center">
            <Spin size="large" />
          </div>
        ) : tableLocations.length > 0 ? (
          <Table
            className="emp-table"
            dataSource={tableLocations}
            rowKey="id"
            pagination={false}
            scroll={{ y: 420 }}
            size="small"
            columns={[
              {
                title: t('attendance.time'),
                dataIndex: 'created_at',
                key: 'created_at',
                width: 110,
                render: (time: string) => dayjs(time).format('HH:mm:ss'),
                sorter: (a: Location, b: Location) => dayjs(b.created_at).unix() - dayjs(a.created_at).unix(),
                defaultSortOrder: 'descend',
              },
              {
                title: t('attendance.location'),
                dataIndex: 'address',
                key: 'address',
                ellipsis: true,
              },
            ]}
          />
        ) : (
          <EmptyState icon={<EnvironmentOutlined />} text={t('attendance.noLocationData')} />
        )}
      </Modal>

      <Modal
        title={t('attendance.closeAttendance')}
        open={closeModalVisible}
        onCancel={() => setCloseModalVisible(false)}
        onOk={handleCloseAttendance}
        confirmLoading={closingLoading}
        okText={t('attendance.closeAttendance')}
        cancelText={t('common.cancel')}
      >
        {selectedAttendanceForClose && (
          <div className="att-close-form">
            <label>
              <span>{t('attendance.checkInTime')}</span>
              <Input value={dayjs(selectedAttendanceForClose.check_in).format('YYYY-MM-DD HH:mm:ss')} disabled />
            </label>
            <label>
              <span>{t('attendance.checkOutTime')}</span>
              <div className="att-close-row">
                <Input value={dayjs(selectedAttendanceForClose.check_in).format('YYYY-MM-DD')} disabled />
                <DatePicker
                  picker="time"
                  value={checkOutTime}
                  onChange={(date) => setCheckOutTime(date)}
                  format="HH:mm:ss"
                  allowClear={false}
                />
              </div>
            </label>
            <label>
              <span>{t('attendance.notes')}</span>
              <TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('attendance.notesPlaceholder')}
                rows={3}
              />
            </label>
          </div>
        )}
      </Modal>

      <Modal
        title={t('attendance.addAttendance')}
        open={newAttendanceModalVisible}
        onCancel={() => {
          setNewAttendanceModalVisible(false);
          newAttendanceForm.resetFields();
        }}
        onOk={() => newAttendanceForm.submit()}
        confirmLoading={creatingAttendance}
        okText={t('attendance.addAttendance')}
        cancelText={t('common.cancel')}
      >
        <Form form={newAttendanceForm} layout="vertical" onFinish={handleCreateAttendance}>
          <Form.Item
            name="date"
            label={t('attendance.date')}
            rules={[{ required: true, message: t('attendance.formValidation.dateRequired') }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={t('attendance.selectDate')} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="checkIn"
                label={t('attendance.checkIn')}
                rules={[{ required: true, message: t('attendance.formValidation.checkInRequired') }]}
              >
                <DatePicker
                  picker="time"
                  style={{ width: '100%' }}
                  format="HH:mm:ss"
                  placeholder={t('attendance.selectCheckInTime')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="checkOut"
                label={t('attendance.checkOut')}
                rules={[{ required: true, message: t('attendance.formValidation.checkOutRequired') }]}
              >
                <DatePicker
                  picker="time"
                  style={{ width: '100%' }}
                  format="HH:mm:ss"
                  placeholder={t('attendance.selectCheckOutTime')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="project"
            label={t('attendance.project')}
            rules={[{ required: true, message: t('attendance.formValidation.projectRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('attendance.selectProject')}
              options={projects.map((project) => ({
                value: project.id,
                label:
                  project.status === 'active'
                    ? project.name
                    : `${project.name} (${t(`projects.statuses.${project.status}`)})`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default AttendanceEmployeeInfo;
